import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Store,
  ClipboardList,
  Receipt,
  Cog,
  LogOut,
  Search,
  CornerDownLeft,
  Gift,
  Undo2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { PERM, posRedirectFor, useCan } from '@/lib/permissions';
import { useShiftAwareLogout } from '@/hooks/useShiftAwareLogout';

interface Command {
  id: string;
  label: string;
  hint: string;
  icon: typeof Store;
  /** The page it opens — hidden when the route gate would not let this account stay. */
  to?: string;
  keywords?: string;
  run: () => void;
}

/**
 * Ctrl/Cmd-K command palette — jump to any surface (or sign out) from anywhere,
 * hands on keys. Type to filter, ↑/↓ to move, Enter to run, Esc to close.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const { requestLogout, dialog: logoutDialog } = useShiftAwareLogout(() => setOpen(false));
  const canVoid = useCan(PERM.VOID_REQUEST);
  const canRefund = useCan(PERM.REFUND_REQUEST);
  const canRequest = canVoid || canRefund;
  const permissions = useAuthStore((s) => s.permissions);
  const wildcards = useAuthStore((s) => s.wildcards);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Global open shortcut. Works even inside inputs, so a clerk can summon it
  // mid-typing. Preventing default stops the browser's own Ctrl-K.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const commands = useMemo<Command[]>(() => {
    const go = (to: string) => () => {
      setOpen(false);
      navigate(to);
    };
    return [
      {
        id: 'menu',
        label: 'Beranda',
        hint: 'Ringkasan hari ini',
        icon: LayoutGrid,
        to: '/menu',
        run: go('/menu'),
      },
      {
        id: 'sell',
        label: 'Jual',
        hint: 'Mulai transaksi baru',
        icon: Store,
        keywords: 'katalog tiket',
        to: '/',
        run: go('/'),
      },
      {
        id: 'foc',
        label: 'Tiket FOC',
        hint: 'Tiket gratis dengan dokumen pendukung',
        icon: Gift,
        keywords: 'foc free of charge gratis undangan',
        to: '/foc',
        run: go('/foc'),
      },
      {
        id: 'orders',
        label: 'Pesanan',
        hint: 'Cari & cetak ulang tiket',
        icon: ClipboardList,
        keywords: 'reprint invoice',
        to: '/orders',
        run: go('/orders'),
      },
      ...(canRequest
        ? [
            {
              id: 'requests',
              label: 'Permintaan Void/Refund',
              hint: 'Status pengajuan ke supervisor',
              icon: Undo2,
              keywords: 'void refund ajuan approval',
              to: '/requests',
              run: go('/requests'),
            },
          ]
        : []),
      {
        id: 'shift',
        label: 'Shift',
        hint: 'Ringkasan & tutup shift',
        icon: Receipt,
        to: '/shift',
        run: go('/shift'),
      },
      {
        id: 'setup',
        label: 'Perangkat',
        hint: 'Atur printer & pembaca kartu',
        icon: Cog,
        keywords: 'printer',
        to: '/setup',
        run: go('/setup'),
      },
      {
        id: 'logout',
        label: 'Keluar',
        hint: 'Akhiri sesi kasir',
        icon: LogOut,
        keywords: 'sign out logout',
        // Same as the rail's "Keluar": asks first while a shift is open.
        run: requestLogout,
      },
    ].filter((c: Command) => !c.to || posRedirectFor({ permissions, wildcards }, c.to) === null);
  }, [navigate, requestLogout, canRequest, permissions, wildcards]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      `${c.label} ${c.hint} ${c.keywords ?? ''}`.toLowerCase().includes(q),
    );
  }, [commands, query]);

  // Reset selection + query each time it opens; focus the field.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      // Radix moves focus on open; grab it back on the next frame.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => setActive(0), [query]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      results[active]?.run();
    }
  };

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          hideClose
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="top-[14%] max-w-lg translate-y-0 gap-0 overflow-hidden border-cream-200 p-0"
        >
          <DialogTitle className="sr-only">Perintah cepat</DialogTitle>
          <DialogDescription className="sr-only">
            Ketik untuk mencari, panah untuk memilih, Enter untuk membuka.
          </DialogDescription>

          {/* Search row */}
          <div className="flex items-center gap-2.5 border-b border-cream-200 px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-ink-400" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ke mana? Ketik untuk mencari…"
              className="min-w-0 flex-1 bg-transparent text-sm text-ink-900 outline-none placeholder:text-ink-400"
              aria-label="Cari perintah"
            />
            <kbd className="rounded bg-cream-100 px-1.5 py-0.5 font-sans text-[10px] font-medium text-ink-400">
              ESC
            </kbd>
          </div>

          {/* Results */}
          {results.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              Tidak ada yang cocok.
            </p>
          ) : (
            <ul ref={listRef} className="max-h-80 overflow-y-auto py-1.5">
              {results.map((c, i) => {
                const Icon = c.icon;
                const isActive = i === active;
                const danger = c.id === 'logout';
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      data-active={isActive}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => c.run()}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                        isActive ? (danger ? 'bg-red-50' : 'bg-cream-100') : 'bg-transparent',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                          danger ? 'bg-red-100 text-crimson-700' : 'bg-cream-100 text-crimson-700',
                          isActive && !danger && 'bg-white',
                        )}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block text-sm font-medium',
                            danger ? 'text-crimson-700' : 'text-ink-900',
                          )}
                        >
                          {c.label}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {c.hint}
                        </span>
                      </span>
                      {isActive && (
                        <CornerDownLeft
                          className="h-3.5 w-3.5 shrink-0 text-ink-400"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
      {logoutDialog}
    </>
  );
}
